<template>
  <div class="super-admin-dashboard">
    <!-- 页面标题 -->
    <a-page-header
      title="系统管理面板"
      sub-title="查看所有租户状态和系统概览"
      style="background: #fff; margin-bottom: 24px; border-radius: 8px;"
    >
      <template #extra>
        <a-button type="primary" @click="refreshData">
          <a-icon type="reload" />刷新数据
        </a-button>
      </template>
    </a-page-header>

    <!-- 统计概览卡片 -->
    <a-row :gutter="24" style="margin-bottom: 24px;">
      <a-col :span="6">
        <a-card>
          <a-statistic
            title="总租户数"
            :value="overviewStats.totalTenants"
            :value-style="{ color: '#1890ff' }"
          >
            <template #prefix>
              <a-icon type="home" />
            </template>
          </a-statistic>
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            title="总用户数"
            :value="overviewStats.totalUsers"
            :value-style="{ color: '#52c41a' }"
          >
            <template #prefix>
              <a-icon type="user" />
            </template>
          </a-statistic>
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            title="总牧场数"
            :value="overviewStats.totalFarms"
            :value-style="{ color: '#722ed1' }"
          >
            <template #prefix>
              <a-icon type="environment" />
            </template>
          </a-statistic>
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            title="总生产个体数"
            :value="overviewStats.totalBreedItems"
            :value-style="{ color: '#fa8c16' }"
          >
            <template #prefix>
              <a-icon type="dot-chart" />
            </template>
          </a-statistic>
        </a-card>
      </a-col>
    </a-row>

    <!-- 租户列表 - 使用 Flex 布局 -->
    <div class="tenant-cards-container">
      <a-spin :spinning="loading">
        <div class="tenant-cards-grid">
          <a-card 
            v-for="item in tenantStats" 
            :key="item.tenant_id"
            class="tenant-card"
            :bordered="true"
            hoverable
          >
            <template #title>
              <div class="tenant-card-header">
                <h3>{{ item.tenant_name }}</h3>
              </div>
            </template>
            
            <a-row :gutter="16">
              <!-- 租户基本信息 -->
              <a-col :span="12">
                <div class="tenant-basic-info">
                  <p><strong>ID:</strong> {{ item.tenant_id }}</p>
                  <p><strong>创建时间:</strong> {{ formatDate(item.created_at) }}</p>
                  <p><strong>订阅到期:</strong> 
                    <span v-if="item.subscription_expired_at">{{ formatDateOnly(item.subscription_expired_at) }}</span>
                    <span v-else style="color: #999;">未订阅</span>
                  </p>
                  <p><strong>状态:</strong> 
                    <a-tag :color="item.status === 2 ? 'green' : 'orange'" style="margin-left: 8px;">
                      {{ item.status_text || (item.status === 2 ? '正常' : '未订阅') }}
                    </a-tag>
                  </p>
                </div>
              </a-col>
              
              <!-- 统计数据 -->
              <a-col :span="12">
                <div class="tenant-stats">
                  <a-statistic
                    title="牧场数量"
                    :value="parseInt(item.farm_count.toString())"
                    :value-style="{ fontSize: '18px', color: '#1890ff' }"
                  />
                  <a-statistic
                    title="用户数量"
                    :value="parseInt(item.user_count.toString())"
                    :value-style="{ fontSize: '18px', color: '#52c41a', marginTop: '8px' }"
                  />
                  <a-statistic
                    title="生产个体数"
                    :value="parseInt((item.total_breed_items || 0).toString())"
                    :value-style="{ fontSize: '18px', color: '#fa8c16', marginTop: '8px' }"
                  />
                </div>
              </a-col>
            </a-row>
            
            <!-- 牧场列表 -->
            <div class="farm-list-section" style="margin-top: 16px;">
              <h4>牧场列表</h4>
              <div v-if="item.farm_list && item.farm_list.length > 0">
                <a-tag 
                  v-for="farm in item.farm_list.slice(0, 3)" 
                  :key="farm.id"
                  color="blue"
                  style="margin-right: 8px; margin-bottom: 4px;"
                >
                  {{ farm.name }}
                </a-tag>
                <span v-if="item.farm_list.length > 3">等{{ item.farm_list.length }}个</span>
              </div>
              <div v-else class="no-data-placeholder">
                暂无牧场数据
              </div>
            </div>
            
            <!-- 活跃度热力图 -->
            <div class="activity-heatmap-section" style="margin-top: 16px;">
              <h4>年度活跃度</h4>
              <div v-if="item.heatmap_data && item.heatmap_data.length > 0">
                <HeatmapCalendar
                  :data="item.heatmap_data"
                  :year="currentYear"
                  :bordered="false"
                  legend-less-text="较少"
                  legend-more-text="较多"
                />
              </div>
              <div v-else class="no-data-placeholder">
                暂无活跃数据
              </div>
            </div>
            
            <!-- 错误信息显示 -->
            <a-alert
              v-if="item.error"
              type="error"
              :message="`数据获取失败: ${item.error}`"
              banner
              style="margin-top: 16px;"
            />
          </a-card>
        </div>
        
        <!-- 空状态 -->
        <div 
          v-if="tenantStats.length === 0 && !loading" 
          class="no-tenants-placeholder"
        >
          <a-icon type="inbox" style="font-size: 48px; color: #ccc; margin-bottom: 16px;" />
          <p>暂无租户数据</p>
        </div>
      </a-spin>
    </div>
  
  <!-- 牧场详情弹窗 -->
  <a-modal
    v-model="farmDetailVisible"
    :title="`牧场详情 - ${currentFarm ? currentFarm.name : ''}`"
    width="600px"
    :footer="null"
    @cancel="closeFarmDetail"
  >
    <div v-if="currentFarm" class="farm-detail-content">
      <!-- 牧场基本信息 -->
      <a-card title="牧场基本信息" size="small" style="margin-bottom: 20px;">
        <a-descriptions :column="1" size="small">
          <a-descriptions-item label="牧场名称">{{ currentFarm.name }}</a-descriptions-item>
          <a-descriptions-item label="牧场编码">{{ currentFarm.code }}</a-descriptions-item>
          <a-descriptions-item label="牧场ID">{{ currentFarm.id }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ formatDate(currentFarm.created_at) }}</a-descriptions-item>
          <a-descriptions-item label="更新时间">{{ formatDate(currentFarm.updated_at) }}</a-descriptions-item>
        </a-descriptions>
      </a-card>
      
      <!-- 统计数据 -->
      <a-card title="统计数据" size="small">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              title="个体数量"
              :value="parseInt(currentFarmStats.breedItemCount.toString())"
              :value-style="{ color: '#1890ff', fontSize: '24px' }"
            >
              <template #prefix>
                <a-icon type="dot-chart" />
              </template>
            </a-statistic>
          </a-col>
          <a-col :span="12">
            <a-statistic
              title="用户数量"
              :value="parseInt(currentFarmStats.userCount.toString())"
              :value-style="{ color: '#52c41a', fontSize: '24px' }"
            >
              <template #prefix>
                <a-icon type="user" />
              </template>
            </a-statistic>
          </a-col>
        </a-row>
      </a-card>
    </div>
  </a-modal>
  
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import HeatmapCalendar from '@/components/Widgets/HeatmapCalendar.vue'

export default {
  name: 'SuperAdminDashboard',
  components: {
    HeatmapCalendar
  },
  data() {
    return {
      loading: false,
      tenantStats: [],
      currentYear: new Date().getFullYear(),
      overviewStats: {
        totalTenants: 0,
        totalUsers: 0,
        totalFarms: 0,
        totalBreedItems: 0
      },
      // 牧场详情弹窗相关
      farmDetailVisible: false,
      currentFarm: null,
      currentFarmStats: {
        breedItemCount: 0,
        userCount: 0
      }
    }
  },
  computed: {
    ...mapGetters('main/user', ['info'])
  },
  async created() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$api.get_dashboard_stats()
        if (res.code === 200) {
          this.tenantStats = res.data
          
          // 计算概览统计数据
          this.overviewStats.totalTenants = parseInt(this.tenantStats.length.toString());
          this.overviewStats.totalUsers = parseInt(this.tenantStats.reduce((sum, tenant) => sum + tenant.user_count, 0).toString());
          this.overviewStats.totalFarms = parseInt(this.tenantStats.reduce((sum, tenant) => sum + tenant.farm_count, 0).toString());
          this.overviewStats.totalBreedItems = parseInt(this.tenantStats.reduce((sum, tenant) => sum + (tenant.total_breed_items || 0), 0).toString());
        } else {
          this.$message.error(res.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取超管首页数据失败:', error)
        this.$message.error('获取数据失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    
    async refreshData() {
      await this.loadData()
      this.$message.success('数据已刷新')
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    
    formatDateOnly(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 显示牧场详情
    async showFarmDetail(farm, tenantId) {
      this.currentFarm = farm;
      this.farmDetailVisible = true;
      
      // 获取牧场统计信息
      try {
        // 调用超级管理员API获取牧场综合统计
        const result = await this.$api.get_farm_stats(tenantId, farm.id);
        
        if (result.code === 200) {
          this.currentFarmStats = {
            breedItemCount: result.data.breedItemCount || 0,
            userCount: result.data.userCount || 0
          };
        } else {
          throw new Error(result.message || '获取数据失败');
        }
      } catch (error) {
        console.error('获取牧场详情失败:', error);
        this.$message.error('获取牧场详情失败: ' + (error.message || '未知错误'));
        // 设置默认值
        this.currentFarmStats = {
          breedItemCount: 0,
          userCount: 0
        };
      }
    },
    
    // 关闭牧场详情弹窗
    closeFarmDetail() {
      this.farmDetailVisible = false;
      this.currentFarm = null;
      this.currentFarmStats = {
        breedItemCount: 0,
        userCount: 0
      };
    }
  }
}
</script>

<style lang="scss" scoped>
.super-admin-dashboard {
  padding: 24px;
  background-color: #f0f2f5;
  min-height: calc(100vh - 64px);
}

.tenant-cards-container {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
}

.tenant-cards-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.tenant-card {
  flex: 1 1 100%; // 单列布局
  min-width: 100%;
  margin-bottom: 0;
}

.tenant-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  
  h3 {
    margin: 0;
    color: #1890ff;
    font-size: 18px;
  }
}

.tenant-basic-info {
  p {
    margin: 4px 0;
    font-size: 14px;
    color: #666;
    display: flex;
    align-items: center;
    
    strong {
      min-width: 70px;
    }
  }
}

.tenant-stats {
  text-align: right;
}

.farm-list-section,
.activity-heatmap-section {
  h4 {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #333;
    font-weight: 600;
  }
}

// 可点击的牧场标签样式
.farm-list-section {
  .ant-tag {
    transition: all 0.3s;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
    }
  }
}

// Ant Design 样式覆盖
:deep(.ant-card-head) {
  border-bottom: 1px solid #f0f0f0;
}

:deep(.ant-card-body) {
  padding: 20px;
}

:deep(.ant-statistic-title) {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

:deep(.ant-statistic-content) {
  font-size: 24px;
  font-weight: 500;
}

// 空数据占位符样式
.no-data-placeholder {
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 24px 0;
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px dashed #d9d9d9;
}

.no-tenants-placeholder {
  text-align: center;
  padding: 48px 0;
  color: #999;
  
  p {
    margin: 0;
    font-size: 16px;
  }
}

// 牧场详情弹窗样式
.farm-detail-content {
  .ant-card {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .ant-descriptions-item-label {
    font-weight: 500;
    color: #666;
  }
  
  .ant-descriptions-item-content {
    color: #333;
  }
}
</style>